import React from "react";
import { Row, Col, Button, Input, Table } from "reactstrap";
import { TextLabel, TextGroup, CustomSelect } from "../../Styled";
import classNames from "classnames";
import { PreviewHead, FixHead } from "./HomeStyled";
import {
  postImportRecords,
  getCustomers,
  getPartsRevs,
  getPartsRevsByCustomerId,
  checkAttritionPackages,
  addColumnAliases,
} from "../../../actions";
import {
  autoMapColumns,
  detectHeaderRow,
  HIGH_CONFIDENCE,
} from "../../../utils/columnAutoMap";
import { connect } from "react-redux";
import { BOM_PARSE } from "../../../constants/urls";
import { toaster } from "../../../function";
import { ToastsStore } from "react-toasts";
import Dropdown from "./../../Dropdown";
import { CloseIcon } from "../../../constants/icons";
import {
  applyImportDigiKeyColumns,
  enrichBomlinesWithDigiKey,
  stripDigiKeyColumns,
} from "../../../constants/digiKeyColumns";
import {
  buildMissingAttritionRows,
  collectBomPackages,
} from "../Attrition/validation";
import { isRealBomLine } from "../../../constants/quoteQuantities";

const attachPartIdsToBomlines = (bomlines, partIds = []) => {
  if (!partIds.length) {
    return bomlines;
  }

  // part_ids follow original import order; map via _bomLineIndex so SMT/THT
  // reordering does not assign the wrong part.
  const realSorted = (bomlines || [])
    .filter(isRealBomLine)
    .map((line, fallbackIndex) => ({
      sortKey: line._bomLineIndex != null ? line._bomLineIndex : fallbackIndex,
    }))
    .sort((a, b) => a.sortKey - b.sortKey);

  const partIdByIndex = {};
  realSorted.forEach((item, i) => {
    const match = partIds[i];
    if (match && match.part_id != null && match.part_id !== "") {
      partIdByIndex[item.sortKey] = Number(match.part_id);
    }
  });

  return (bomlines || []).map((line, arrayIndex) => {
    if (!isRealBomLine(line)) {
      return line;
    }

    const key = line._bomLineIndex != null ? line._bomLineIndex : arrayIndex;
    if (partIdByIndex[key] == null) {
      return line;
    }

    return { ...line, part_id: partIdByIndex[key] };
  });
};

const ColumnOptions = ({
  columns,
  filterColumns,
  columnIndex,
  selectedColumn,
}) => {
  let checkValue = selectedColumn.find((item) => {
    return item.columnIndex === columnIndex;
  });

  const isIgnored = !!checkValue && checkValue.value === "ignore";
  const isLowConfidence =
    !!checkValue &&
    !isIgnored &&
    checkValue.autoScore != null &&
    checkValue.autoScore < HIGH_CONFIDENCE;

  let classActive = classNames(
    { "bg-green": checkValue && !isLowConfidence },
    { "auto-review": isLowConfidence },
    { ignored: isIgnored }
  );

  return (
    <Input
      type="select"
      onChange={(e) => filterColumns(e.target.value, columnIndex)}
      className={classNames("p-table", classActive)}
      title={
        checkValue && checkValue.autoScore != null
          ? `Auto-matched (${checkValue.autoScore}% confidence)`
          : undefined
      }
      // value={columns.find(item => checkValue !== undefined && checkValue.value === item.code_name)?.code_name || ""}
    >
      {columns.map((item, index) => {
        if (checkValue !== undefined && checkValue.value === item.code_name) {
          return (
            <option
              key={index}
              selected
              value={item.code_name}
              disabled={item.disabled}
            >
              {item.name}
            </option>
          );
        }
        return (
          <option key={index} value={item.code_name} disabled={item.disabled}>
            {item.name}
          </option>
        );
      })}
    </Input>
  );
};

class ImportPreview extends React.Component {
  constructor(props) {
    super(props);
    this.sheetRef = React.createRef();
    this.customerRef = React.createRef();
    this.partRevRef = React.createRef();

    this.state = {
      columns: props.columns,
      selectedColumn: [],
      customer: "",
      sheets: "",
      part: "",
      start_line: "",
      importBtnEnable: true,
      bomData: [...props.data],
      fileName: [...props.fileName],
      isSheetOpen: false,
      isOpen: false,
      isPartREVOpen: false,
      partRevs: [],
      toggleModal: props.toggleModal,
      toggleHistoryModal: props.toggleHistoryModal,
    };
  }

  componentDidMount() {
    this.initFromData(this.props.data);
    Promise.resolve(this.props.getCustomers(this.props.user.token)).catch(() => {});
    Promise.resolve(this.props.getPartsRevs(this.props.user.token)).catch(() => {});
  }

  initFromData = (data) => {
    const rows = data || [];
    const headerIndex = detectHeaderRow(rows, this.props.columnAliases);
    const bomData = headerIndex > 0 ? rows.slice(headerIndex + 1) : [...rows];
    this.setState({
      bomData,
      start_line: headerIndex > 0 ? String(headerIndex + 2) : "",
    });
    this.applyAutoMap(rows.slice(headerIndex));
  };

  applyAutoMap = (rows) => {
    const columns = this.state.columns;
    columns.forEach((item) => {
      item.disabled = false;
    });

    if (!rows || !rows.length) {
      this.setState({ columns, selectedColumn: [] });
      return;
    }

    const headers = Object.values(rows[0]);
    const suggestions = autoMapColumns(
      headers,
      rows.slice(1),
      this.props.columnAliases,
      columns.map((item) => item.code_name)
    );

    const selectedColumn = suggestions.map(({ field, score }, index) => {
      const column = columns.find((item) => item.code_name === field);
      const header = headers[index];
      if (field === "ignore" || !column) {
        return { columnIndex: index + 1, value: "ignore", header };
      }
      column.disabled = true;
      return {
        columnIndex: index + 1,
        name: column.name,
        value: field,
        autoScore: score,
        header,
      };
    });

    this.setState({ columns, selectedColumn });
  };

  componentWillUnmount() {
    this.props.columns.map((item) => {
      item.disabled = false;
      return item;
    });
    this.setState({
      columns: this.props.columns,
      selectedColumn: [],
      name: "",
      part: "",
      importBtnEnable: true,
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.data !== prevProps.data) {
      this.initFromData(this.props.data);
    }
    if (this.props.newCustomer !== prevProps.newCustomer) {
      this.setState({ customer: this.props.newCustomer });
    }
    if (this.props.newPartRev !== prevProps.newPartRev) {
      this.setState({ part: this.props.newPartRev });
      this.handleChangePartREV(null, this.props.newPartRev);
    }
  }

  shouldComponentUpdate(prev, next) {
    if (prev.customer !== next.customer) {
      return true;
    }
    return false;
  }

  filterColumns = (e, columnIndex) => {
    let columns = this.state.columns;
    let selectedColumn = this.state.selectedColumn;
    var needEnable = "";
    let columnsFilters = columns.find((item) => item.code_name === e);

    selectedColumn.map((item) => {
      if (item.columnIndex === columnIndex) {
        needEnable = item.value;
        item.value = e;
        item.name = columnsFilters.name;
        item.autoScore = undefined;
        item.userPicked = true;
      }
      return item;
    });

    columns.map((item) => {
      if (item.code_name === e && e !== "ignore") {
        item.disabled = true;
      }

      if (item.code_name === needEnable) {
        item.disabled = false;
      }

      return item;
    });
    this.setState({ columns });
  };

  handleChange = (e) => {
    this.setState({
      [e.target.name]: e.target.value,
    });

    this.arraySplice(e);
  };

  handleClickOutside = (key) => {
    this.setState({
      [key]: false,
    });
  };

  handleChangeSheet = (e, selected) => {
    this.props.fileUpload(this.props.file, selected.id);
    this.setState({
      sheets: selected,
      isSheetOpen: false,
    });
  };

  handleChangeCustomer = (e, selected) => {
    Promise.resolve(
      this.props.getPartsRevsByCustomerId(this.props.user.token, selected.id)
    ).catch(() => {});
    localStorage.setItem("selectedCustomer", JSON.stringify(selected));
    this.setState({
      customer: selected,
      isOpen: false,
      part: "",
    });
  };

  handleChangePartREV = (e, selected) => {
    this.setState({
      part: selected,
      isPartREVOpen: false,
      selectedPartRevId: selected.id,
    });
  };

  arraySplice = (e) => {
    if (parseInt(e.target.value) === 0 || (e.target.value && parseInt(e.target.value) > this.props.data.length)) {
      this.setState({ bomData: this.props.data });
      toaster(ToastsStore, "error", "Please fill valid start line.");
      return;
    }

    let data = [...this.props.data].slice(
      parseInt(e.target.value) - 1,
      this.props.data.length
    );
    this.setState({ bomData: data });
  };

  filterToSelectedColumn = async (data, selectedDataValue) => {
    return await data.map((item) => {
      return Object.keys(item).forEach((col) => {
        if (!selectedDataValue.includes(col)) {
          delete item[col];
        }
        return item;
      });
    });
  };

  validateDesignatorAndQuantity = (removeIgnoreColumns) => {
    let invalidQuantityColumns = [];
    removeIgnoreColumns.forEach((row, index) => {
      if (row.designator != null) {
        let designator = String(row.designator);
        designator = designator.replace(/\s/g, "");
        designator = designator.split(",");

        let designatorCount = 0;
        let singleDesignatorRegExp = new RegExp("^@?[a-zA-Z]+\\d+$");
        let rangeDesignatorRegExp = new RegExp(
          "^@?[a-zA-Z]+\\d+[-]@?[a-zA-Z]*\\d+$"
        );
        var numberPatternRegExp = /\d+/g;

        designator.forEach((d) => {
          if (singleDesignatorRegExp.test(d)) {
            designatorCount++;
          } else if (rangeDesignatorRegExp.test(d)) {
            let rangeNumbers = d.match(numberPatternRegExp);
            let rangeCount = rangeNumbers[1] - rangeNumbers[0] + 1;
            if (rangeCount > 0) {
              designatorCount += rangeCount;
            }
          }
        });

        if (row.quantity !== designatorCount) {
          invalidQuantityColumns.push(index + 1);
        }
      }
    });

    if (invalidQuantityColumns.length > 0) {
      alert(
        "Quantity is either NaN or does not match with the Reference Designator for BOM lines (shown in maroon): " +
          invalidQuantityColumns.join(", ") +
          "." +
          "\n\nWe will still import the file and use qauntity as the source of truth, please rectify and import again if you wish."
      );
    }

    return invalidQuantityColumns;
  };

  importData = async () => {
    let columnsData = this.state.bomData;
    let changeColumnIndex = [];
    let ignoreColumnsIndex = [];
    let dataColumns = Object.keys(columnsData[0]);
    let requireFields = ["manufacturer_part_number", "quantity"];
    let fieldCount = 0;
    // fields validation
    if (!this.state.sheets || !this.state.customer || !this.state.part) {
      return toaster(
        ToastsStore,
        "error",
        "Please select sheet, customer name and part #REV."
      );
    }

    this.state.selectedColumn.map((item) => {
      if (requireFields.includes(item.value)) {
        fieldCount++;
      }

      if (item.value === "ignore") {
        return ignoreColumnsIndex.push(dataColumns[item.columnIndex - 1]); // get ignore column name
      } else {
        return changeColumnIndex.push(item.columnIndex); // set changed column index
      }
    });

    if (fieldCount !== requireFields.length) {
      return toaster(
        ToastsStore,
        "error",
        "Part# and Quantity must be selected."
      );
    }

    const learnedAliases = {};
    this.state.selectedColumn.forEach((item) => {
      const header = item.header == null ? "" : String(item.header).trim();
      if (item.userPicked && item.value !== "ignore" && header) {
        learnedAliases[item.value] = header;
      }
    });

    let changeColumnRows = this.state.selectedColumn
      .filter((item) => item.value !== "ignore")
      .map(({ columnIndex, name, value }) => ({ columnIndex, name, value }));
    let removeIgnoreColumns = columnsData.map((item) => {
      Object.keys(item).forEach((col, index) => {
        let change_column_name = changeColumnRows.find(
          (x) => x.columnIndex === index + 1
        );
        if (change_column_name) {
          item[change_column_name.value] = item[col];
          if (change_column_name.value !== col) {
            delete item[col];
          }
        }
      });
      ignoreColumnsIndex.forEach((e) => delete item[e]);

      return item;
    });

    let invalidQuantityColumns = this.validateDesignatorAndQuantity(
      removeIgnoreColumns
    );

    let selectedDataValue = this.state.selectedColumn.map(({ value }) => value);
    await this.filterToSelectedColumn(removeIgnoreColumns, selectedDataValue);

    // const combinedBomLines = removeIgnoreColumns.reduce((accumulator, current) => {
    //   const existingObject = accumulator.find(obj => {
    //       if(obj.manufacturer === current.manufacturer && obj.manufacturer_part_number === current.manufacturer_part_number){
    //           return true
    //       }else{
    //           return false
    //       }
    //   });
    //   if (existingObject) {
    //     // Object.assign(existingObject, {...current, quantity: existingObject.quantity + current.quantity, designator: existingObject.designator + ", " + current.designator});
    //     Object.assign(existingObject, {...current, quantity: existingObject.quantity + current.quantity, designator: existingObject.designator, added_designator: (existingObject.added_designator ? existingObject.added_designator + ", " : "") + current.designator});
    //   } else {
    //     accumulator.push(current);
    //   }
    //   return accumulator;
    // }, []);

    // Create a new array with the designator_color property added based on the condition
    const updatedRemoveIgnoreColumns = removeIgnoreColumns.map(item => {
        // Check if customer_part_number or (manufacturer and manufacturer_part_number) or reference designator are the same
        const isSameManufacturer = removeIgnoreColumns.some(otherItem =>
            otherItem !== item &&
            (((item?.customer_part_number) && (otherItem.customer_part_number === item.customer_part_number)) ||
            (otherItem.manufacturer === item.manufacturer &&
            otherItem.manufacturer_part_number === item.manufacturer_part_number) ||
            ((item?.designator) && (otherItem.designator.replace(/\s+/g, '') === item.designator.replace(/\s+/g, ''))))
        );

        // If they are the same, add designator_color: 'plum'
        if (isSameManufacturer) {
            return { ...item, designator_color: 'plum' };
        }

        return item;
    });

    let importRecord = {
      bomlines: updatedRemoveIgnoreColumns,
      invalidQuantityColumns: invalidQuantityColumns,
      date: new Date(),
      name: this.state.customer.name,
      part: this.state.part.name,
      selectedColumns: changeColumnRows,
    };
    const formData = new FormData();
    formData.append("file", this.props.file[0], this.props.file[0].name);
    formData.append("bomlines", JSON.stringify(updatedRemoveIgnoreColumns));
    formData.append("part_rev_id", this.state.selectedPartRevId);
    const enrichmentProgressToken = `imp-${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
    formData.append("enrichment_progress_token", enrichmentProgressToken);

    const storeBOMParse = async () => {
      this.setState({ importBtnEnable: false });
      this.props.toggleModal();
      this.props.toggleHistoryModal();
      if (this.props.setLoadingMessage) {
        this.props.setLoadingMessage("Importing BOM — DigiKey enrichment…");
      }
      if (this.props.setEnrichmentProgress) {
        this.props.setEnrichmentProgress({
          total: 0,
          succeeded: 0,
          failed: 0,
          in_progress: 0,
          percent: 1,
          message: "Preparing BOM import…",
        });
      }
      if (this.props.setLoading) {
        this.props.setLoading(true);
      }

      const applyProgress = (progress) => {
        if (!progress || typeof progress !== "object") return;
        if (this.props.setEnrichmentProgress) {
          this.props.setEnrichmentProgress({
            total: Number(progress.total) || 0,
            succeeded: Number(progress.succeeded) || 0,
            failed: Number(progress.failed) || 0,
            in_progress: Number(progress.in_progress) || 0,
            percent: Number(progress.percent) || 0,
            message: progress.message || "",
            status: progress.status || "running",
          });
        }
        if (this.props.setLoadingPercent && Number(progress.percent) > 0) {
          this.props.setLoadingPercent(Number(progress.percent));
        }
        if (this.props.setLoadingMessage && progress.message) {
          this.props.setLoadingMessage(progress.message);
        }
      };

      try {
        const res = await fetch(BOM_PARSE, {
          method: "POST",
          headers: {
            authorization: "Bearer " + this.props.user.token,
            Accept: "application/x-ndjson, application/json",
          },
          body: formData,
        });

        const contentType = (res.headers.get("content-type") || "").toLowerCase();
        let data = null;

        // Streamed NDJSON progress (same connection — works with single-threaded PHP).
        if (contentType.includes("ndjson") && res.body && res.body.getReader) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (let i = 0; i < lines.length; i++) {
              const line = lines[i].trim();
              if (!line) continue;
              let evt;
              try {
                evt = JSON.parse(line);
              } catch (_parseErr) {
                continue;
              }
              if (evt.type === "progress") {
                applyProgress(evt);
              } else if (evt.type === "complete") {
                data = evt;
              } else if (evt.type === "error") {
                throw new Error(evt.message || "BOM import failed.");
              }
            }
          }
          if (!data) {
            throw new Error(
              res.ok
                ? "Import failed: incomplete server response."
                : `Import failed (${res.status}).`,
            );
          }
        } else {
          // Legacy JSON response fallback
          try {
            data = await res.json();
          } catch (parseError) {
            throw new Error(
              res.ok
                ? "Import failed: invalid server response."
                : `Import failed (${res.status}). The server may have timed out during DigiKey enrichment — please try again.`,
            );
          }
        }

        if (!res.ok) {
          throw new Error(data.message || `Import failed (${res.status}).`);
        }

        if (data.code >= 200 && data.code < 400) {
          importRecord.bomId = data.data.bom_id;
          importRecord.issue_report = data.data.issue_report || null;
          importRecord.bomlines = stripDigiKeyColumns(importRecord.bomlines);
          const partIds = data.data.part_ids || [];

          const enrichment = data.data.digikey_enrichment;
          if (enrichment && enrichment.enriched > 0) {
            // Keep original order for quote index mapping; organize display BOM separately
            importRecord.excelData = attachPartIdsToBomlines(
              enrichBomlinesWithDigiKey(importRecord.bomlines, enrichment),
              partIds
            );
            importRecord.bomlines = attachPartIdsToBomlines(
              applyImportDigiKeyColumns(importRecord.bomlines, enrichment),
              partIds
            );
          } else {
            importRecord.excelData = attachPartIdsToBomlines(
              importRecord.bomlines.map((line, index) => ({
                ...line,
                _bomLineIndex: index,
              })),
              partIds
            );
            importRecord.bomlines = attachPartIdsToBomlines(
              importRecord.bomlines.map((line, index) => ({
                ...line,
                _bomLineIndex: index,
              })),
              partIds
            );
            const reason =
              (enrichment && enrichment.error) ||
              (enrichment && enrichment.failed > 0
                ? `DigiKey enrichment failed for ${enrichment.failed} part(s)`
                : "Unable to find DigiKey data");
            toaster(ToastsStore, "warning", reason);
          }

          this.props.postImportRecords(this.props.user.token, importRecord);

          if (Object.keys(learnedAliases).length) {
            Promise.resolve(
              this.props.addColumnAliases(this.props.user.token, learnedAliases)
            ).catch(() => {});
          }

          localStorage.setItem(
            "importColumn",
            Object.keys(
              importRecord.bomlines.find((line) => line.manufacturer_part_number) ||
                importRecord.bomlines[0] ||
                {}
            ).length
          );
          this.props.showBomLines(true);
          this.setState({
            selectedColumn: [],
            customer: "",
            part: "",
          });
          toaster(ToastsStore, "success", "Product imported successfully.");
          this.checkMissingAttritions(importRecord.bomlines);
          if (this.props.onObsoletePartsReview) {
            this.props.onObsoletePartsReview(importRecord.bomlines);
          }
        } else {
          throw new Error(data.message || "Import failed.");
        }
        this.setState({ importBtnEnable: true });
      } catch (e) {
        console.error(e);
        toaster(
          ToastsStore,
          "error",
          e.message || "BOM import failed. Please try again."
        );
        this.setState({ importBtnEnable: true });
      } finally {
        if (this.props.setEnrichmentProgress) {
          this.props.setEnrichmentProgress(null);
        }
        if (this.props.setLoading) {
          this.props.setLoading(false);
        }
      }
    };

    storeBOMParse();
  };

  checkMissingAttritions = async (bomlines) => {
    const roleId = this.props.user?.user?.role_id;
    if (roleId !== 1 && roleId !== 2) {
      return;
    }
    if (!this.props.onMissingAttritions) {
      return;
    }

    try {
      const { packages, leadByPackage } = collectBomPackages(bomlines);
      if (!packages.length) {
        return;
      }

      const res = await this.props.checkAttritionPackages(
        this.props.user.token,
        packages
      );
      if (!(res && res.code >= 200 && res.code < 400)) {
        return;
      }

      const missingPackages = Array.isArray(res.data) ? res.data : [];
      if (!missingPackages.length) {
        return;
      }

      const rows = buildMissingAttritionRows(missingPackages, leadByPackage);
      if (rows.length > 0) {
        this.props.onMissingAttritions(rows);
      }
    } catch (e) {
      console.log(e);
    }
  };

  render() {
    let ingnorColumns = this.state.selectedColumn.map((e) => {
      return e.value === "ignore" ? e.columnIndex : false;
    });

    const previewRows = Array.isArray(this.state.bomData)
      ? this.state.bomData
      : [];
    const columnKeys = previewRows[0] ? Object.keys(previewRows[0]) : [];

    return (
      <React.Fragment>
        <PreviewHead>
          <Row className="align-items-end">
            <span
              className="importCloseIcon"
              onClick={() => this.state.toggleModal()}
            >
              <CloseIcon />
            </span>
          </Row>
          <Row className="align-items-end">
            <Col lg="4">
              <div className="successAlert">
                <h5>
                  <img src="/images/CheckCircle.png" alt="CheckCircle" />
                  XLSX BOM File loaded Successfully.
                </h5>
                <span>(Total Rows : {this.state.bomData.length})</span>
              </div>
              <div className="fileInfo">
                <h5>
                  <img src="/images/Shape-fill.png" alt="excel_icon" />
                  <div>
                    <span>BOM Name</span>
                    <span>{this.state.fileName}</span>
                  </div>
                </h5>
              </div>
            </Col>
            <Col md="8" lg="6" xl="5" className="offset-xl-1">
              <Row>
              <Col>
                  <TextGroup>
                    <TextLabel className="d-block">
                      Select Sheet
                    </TextLabel>
                    <div className="clearfix"></div>
                    <CustomSelect>
                      <div
                        className="form-control p-form"
                        onClick={() =>
                          this.setState({ isSheetOpen: !this.state.isSheetOpen })
                        }
                      >
                        {this.state.sheets !== ""
                          ? this.state.sheets.name
                          : "Select Sheet"}
                      </div>
                      <Dropdown
                        data={this.props.sheets}
                        isOpen={this.state.isSheetOpen}
                        type="isSheetOpen"
                        ref={this.sheetRef}
                        placeholder="Select Sheet"
                        click={this.handleClickOutside}
                        handleChangeEvent={this.handleChangeSheet}
                        name={this.state.sheets}
                      />
                    </CustomSelect>
                  </TextGroup>
                </Col>
                <Col>
                  <TextGroup>
                    <TextLabel className="d-block">
                      Customer Name
                      <span
                        className="addNewBtn"
                        onClick={this.props.addNewForm}
                      >
                        Add New
                      </span>
                    </TextLabel>
                    <div className="clearfix"></div>
                    <CustomSelect>
                      <div
                        className="form-control p-form"
                        onClick={() =>
                          this.setState({ isOpen: !this.state.isOpen })
                        }
                      >
                        {this.state.customer !== ""
                          ? this.state.customer.name
                          : "Select Customer"}
                      </div>
                      <Dropdown
                        data={this.props.customers}
                        isOpen={this.state.isOpen}
                        type="isOpen"
                        ref={this.customerRef}
                        placeholder="Select customer"
                        click={this.handleClickOutside}
                        handleChangeEvent={this.handleChangeCustomer}
                        name={this.state.customer}
                      />
                    </CustomSelect>
                  </TextGroup>
                </Col>
                <Col>
                  {this.state.customer && (
                    <TextGroup>
                      <TextLabel className="d-block">
                        Part #REV
                        {this.state.customer !== "" && (
                          <span
                            className="addNewBtn"
                            onClick={this.props.addNewPartRevsForm}
                          >
                            Add New
                          </span>
                        )}
                      </TextLabel>
                      <div className="clearfix"></div>
                      <CustomSelect>
                        <div
                          className="form-control p-form"
                          onClick={() =>
                            this.setState({
                              isPartREVOpen: !this.state.isPartREVOpen,
                            })
                          }
                        >
                          {this.state.part !== ""
                            ? this.state.part.name
                            : "Select PartRevs"}
                        </div>
                        <Dropdown
                          data={this.props.partRevs}
                          isOpen={this.state.isPartREVOpen}
                          type="isPartREVOpen"
                          ref={this.partRevRef}
                          placeholder="Select part"
                          click={this.handleClickOutside}
                          handleChangeEvent={this.handleChangePartREV}
                          name={this.state.part}
                        />
                      </CustomSelect>
                    </TextGroup>
                  )}
                </Col>
              </Row>
            </Col>
            <Col md="4" lg="2">
              <TextGroup>
                <div className="xl-flex">
                  <TextLabel>Start Line</TextLabel>
                  <Input
                    value={this.state.start_line || ""}
                    type="text"
                    placeholder="Enter Start Line"
                    name="start_line"
                    onChange={(e) => this.handleChange(e)}
                  />
                </div>
              </TextGroup>
              <TextGroup>
                <Button
                  className="btn-block importBtn blue p-form"
                  onClick={this.importData}
                  disabled={!this.state.importBtnEnable}
                >
                  Import BOM
                </Button>
              </TextGroup>
            </Col>
          </Row>
        </PreviewHead>
        <div className="table-responsive mh-430">
          <Table className="table-bordered hovered">
            <thead>
              {columnKeys.length > 0 && (
                <React.Fragment>
                  <FixHead>
                    {columnKeys.map((column, index) => {
                      return (
                        <th className="p-0" key={index}>
                          <CustomSelect>
                            <ColumnOptions
                              columnIndex={index + 1}
                              columns={this.state.columns}
                              filterColumns={this.filterColumns}
                              selectedColumn={this.state.selectedColumn}
                            />
                          </CustomSelect>
                        </th>
                      );
                    })}
                  </FixHead>
                  <tr>
                    {columnKeys.map((column, index) => {
                      return (
                        <th
                          key={index}
                          className={classNames({
                            ignored: ingnorColumns.includes(index + 1),
                          })}
                        >
                          {column}
                        </th>
                      );
                    })}
                  </tr>
                </React.Fragment>
              )}
            </thead>
            <tbody>
              {columnKeys.length === 0 ? (
                <tr>
                  <td>This sheet has no data.</td>
                </tr>
              ) : (
                previewRows.map((rows, index) => {
                  return (
                    <tr key={index}>
                      {Object.keys(rows).map((column, index) => {
                        return (
                          <td
                            key={index}
                            className={classNames({
                              ignored: ingnorColumns.includes(index + 1),
                            })}
                          >
                            {rows[column]}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </React.Fragment>
    );
  }
}
const mapStateToProps = (state) => ({
  user: state.user,
  customers: state.customers,
  partRevs: state.partRevs,
});
export default connect(mapStateToProps, {
  postImportRecords,
  getCustomers,
  getPartsRevs,
  getPartsRevsByCustomerId,
  checkAttritionPackages,
  addColumnAliases,
})(ImportPreview);
